import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { Location } from '@angular/common';
import { FormBuilder, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { Horario } from 'src/app/models/Horario';
import { UsuarioService } from 'src/app/services/usuario.service';
import { LoadingService } from 'src/app/services/loading.service';
import { AuthService } from 'src/app/services/auth.service';
import { firstValueFrom } from 'rxjs';

type HorariosForm = {
  lunes_desde: string;
  lunes_hasta: string;
  martes_desde: string;
  martes_hasta: string;
  miercoles_desde: string;
  miercoles_hasta: string;
  jueves_desde: string;
  jueves_hasta: string;
  viernes_desde: string;
  viernes_hasta: string;
  sabado_desde: string;
  sabado_hasta: string;
  domingo_desde: string;
  domingo_hasta: string;
  feriados_abierto: boolean;
};

@Component({
    selector: 'app-my-account-horarios',
    templateUrl: './my-account-horarios.component.html',
    styleUrls: ['./my-account-horarios.component.css'],
    standalone: false
})
export class MyAccountHorariosComponent implements OnInit{
  private _router = inject(Router);
  private _usuarioService = inject(UsuarioService);
  private _fb = inject(FormBuilder);
  private _location = inject(Location);
  private _loadingService = inject(LoadingService);
  private _authService = inject(AuthService);

  // estado
  error = signal<string>('');
  loading = computed(() => this._loadingService.isLoading());

  // id horario si existe (para update)
  private horarioId = signal<number | null>(null);

  // usuario
  private id_usuario = computed(() => this._usuarioService.usuario()?.id ?? '');

  // form
  readonly form = this._fb.nonNullable.group({
    lunes_desde: this._fb.nonNullable.control('', Validators.required),
    lunes_hasta: this._fb.nonNullable.control('', Validators.required),
    martes_desde: this._fb.nonNullable.control('', Validators.required),
    martes_hasta: this._fb.nonNullable.control('', Validators.required),
    miercoles_desde: this._fb.nonNullable.control('', Validators.required),
    miercoles_hasta: this._fb.nonNullable.control('', Validators.required),
    jueves_desde: this._fb.nonNullable.control('', Validators.required),
    jueves_hasta: this._fb.nonNullable.control('', Validators.required),
    viernes_desde: this._fb.nonNullable.control('', Validators.required),
    viernes_hasta: this._fb.nonNullable.control('', Validators.required),
    sabado_desde: this._fb.nonNullable.control('', Validators.required),
    sabado_hasta: this._fb.nonNullable.control('', Validators.required),
    domingo_desde: this._fb.nonNullable.control('', Validators.required),
    domingo_hasta: this._fb.nonNullable.control('', Validators.required),
    feriados_abierto: this._fb.nonNullable.control(true, Validators.required),
  });

  async ngOnInit(): Promise<void> {
    await this._loadingService.run(async () => {
      try {
        await this._authService.ensureSession();
      } catch (e) {
        this._router.navigate(['/sign-in']);
        return;
      }

      const userId = this.id_usuario();
      if (!userId) {
        this._router.navigate(['/sign-in']);
        return;
      }

      // Traer horario actual (si no existe, no lo tomamos como error “fatal”)
      try {
        const res = await firstValueFrom(this._usuarioService.getHorario(userId));
        // Si no existe horario → modo crear
        if (!res) {
          this.horarioId.set(null);
          this.form.reset({
            lunes_desde: '',
            lunes_hasta: '',
            martes_desde: '',
            martes_hasta: '',
            miercoles_desde: '',
            miercoles_hasta: '',
            jueves_desde: '',
            jueves_hasta: '',
            viernes_desde: '',
            viernes_hasta: '',
            sabado_desde: '',
            sabado_hasta: '',
            domingo_desde: '',
            domingo_hasta: '',
            feriados_abierto: false
          });
          return;
        }

        // Si existe horario → modo editar
        this.horarioId.set(res.id!);

        this.form.patchValue({
          lunes_desde: res.x_lunes_desde ?? '',
          lunes_hasta: res.x_lunes_hasta ?? '',
          martes_desde: res.x_martes_desde ?? '',
          martes_hasta: res.x_martes_hasta ?? '',
          miercoles_desde: res.x_miercoles_desde ?? '',
          miercoles_hasta: res.x_miercoles_hasta ?? '',
          jueves_desde: res.x_jueves_desde ?? '',
          jueves_hasta: res.x_jueves_hasta ?? '',
          viernes_desde: res.x_viernes_desde ?? '',
          viernes_hasta: res.x_viernes_hasta ?? '',
          sabado_desde: res.x_sabado_desde ?? '',
          sabado_hasta: res.x_sabado_hasta ?? '',
          domingo_desde: res.x_domingo_desde ?? '',
          domingo_hasta: res.x_domingo_hasta ?? '',
          feriados_abierto: !!res.b_feriados_abierto,
        });
      } catch {
        // si no existe horario, dejamos el form vacío (create)
        this.horarioId.set(null);
      }
    });
  }

  async saveHorarios(): Promise<void> {
    this.error.set('');

    if (this.loading() || this.form.pending) return;

    this.form.markAllAsTouched();

    // validaciones “a mano” (mismo comportamiento que tu versión vieja)
    const v = this.form.getRawValue();

    const checks: Array<[boolean, string]> = [
      [!v.lunes_desde || !v.lunes_hasta, 'Debe Completar el horario del Lunes'],
      [!v.martes_desde || !v.martes_hasta, 'Debe Completar el horario del Martes'],
      [!v.miercoles_desde || !v.miercoles_hasta, 'Debe Completar el horario del Miércoles'],
      [!v.jueves_desde || !v.jueves_hasta, 'Debe Completar el horario del Jueves'],
      [!v.viernes_desde || !v.viernes_hasta, 'Debe Completar el horario del Viernes'],
      [!v.sabado_desde || !v.sabado_hasta, 'Debe Completar el horario del Sábado'],
      [!v.domingo_desde || !v.domingo_hasta, 'Debe Completar el horario del Domingo'],
    ];

    const firstError = checks.find(([cond]) => cond);
    if (firstError) {
      this.error.set(firstError[1]);
      return;
    }

    if (this.form.invalid) {
      this.error.set('Por favor, completá los campos requeridos.');
      return;
    }

    const userId = this.id_usuario();
    if (!userId) {
      this.error.set('Sesión inválida. Volvé a iniciar sesión.');
      this._router.navigate(['/sign-in']);
      return;
    }

    const horario: Horario = {
      id_usuario: userId,
      x_lunes_desde: v.lunes_desde,
      x_lunes_hasta: v.lunes_hasta,
      x_martes_desde: v.martes_desde,
      x_martes_hasta: v.martes_hasta,
      x_miercoles_desde: v.miercoles_desde,
      x_miercoles_hasta: v.miercoles_hasta,
      x_jueves_desde: v.jueves_desde,
      x_jueves_hasta: v.jueves_hasta,
      x_viernes_desde: v.viernes_desde,
      x_viernes_hasta: v.viernes_hasta,
      x_sabado_desde: v.sabado_desde,
      x_sabado_hasta: v.sabado_hasta,
      x_domingo_desde: v.domingo_desde,
      x_domingo_hasta: v.domingo_hasta,
      b_feriados_abierto: v.feriados_abierto,
    };

    await this._loadingService.run(async () => {
      try {
        this.form.disable({ emitEvent: false });

        const id = this.horarioId();
        if (id) {
          horario.id = id;
          await firstValueFrom(this._usuarioService.updateHorario(horario));
        } else {
          const created = await firstValueFrom(this._usuarioService.createHorario(horario));
          // si el backend devuelve id, lo guardamos para próximos updates
          if ((created as any)?.id) this.horarioId.set((created as any).id);
        }

        await this._router.navigate(['/my-account']);
      } catch (e) {
        console.error(e);
        this.error.set('Error al guardar el horario');
      } finally {
        this.form.enable({ emitEvent: false });
      }
    });
  }

  back(){
    this._location.back();
  }
}
